import React, { useEffect, useState } from "react";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { FaDownload } from "react-icons/fa6";
import { RiFullscreenExitLine, RiFullscreenLine, RiResetRightLine } from "react-icons/ri";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { isBrowser } from "~/utils/envirement";

import "./ControlBar.css";
import { useFontStyle } from "~/hook/useFontStyle";

interface ControlBarProps {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomSlider: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFit: () => void;
  onReset: () => void;
  onFullscreen: () => void;
  zoomSliderValue: number;
  downloadUrl?: string;
  downloadUrlHigh?: string;
  isWeb: boolean;
  isDarkMode?: boolean;
}

const ControlBar: React.FC<ControlBarProps> = ({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onZoomSlider,
  onFit,
  onReset,
  onFullscreen,
  zoomSliderValue,
  downloadUrl,
  downloadUrlHigh,
  isWeb,
  isDarkMode = false,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const { normalInter, medium } = useFontStyle();

  useEffect(() => {
    if (!isBrowser()) return;

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const handleFullscreenClick = () => {
    onFullscreen();
  };

  const downloadImage = async (url: string | undefined, quality: string) => {
    if (!url) {
      console.error("Download URL is not available");
      return;
    }

    setIsDownloading(true);
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const urlObj = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlObj;
      link.download = url.split("/").pop() || `image-${quality}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(urlObj);
      setShowDownloadDialog(false);
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      // Reset spinner after 1 second
      setTimeout(() => {
        setIsDownloading(false);
      }, 1000);
    }
  };

  const handleDownloadClick = () => {
    // Show dialog if both URLs exist, otherwise download directly
    if (downloadUrlHigh && downloadUrl) {
      setShowDownloadDialog(true);
    } else if (downloadUrl) {
      downloadImage(downloadUrl, "normal");
    }
  };

  return (
    <div className={`bottom-control-bar ${isDarkMode ? "dark-mode" : ""}`}>
      {/* Left side - Zoom Out */}
      <button className="control-icon-btn zoom-out-btn" onClick={onZoomOut} title="Zoom Out" aria-label="Zoom out">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
          <line x1="7" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth="2" />
          <line x1="14" y1="14" x2="20" y2="20" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>

      {/* Zoom Slider */}
      <div className="zoom-slider-container">
        <input
          type="range"
          className="zoom-slider-bottom"
          min="0"
          max="100"
          value={Math.max(0, Math.min(100, zoomSliderValue))}
          onChange={onZoomSlider}
          title="Zoom Level"
          aria-label="Zoom slider"
        />
        <span className="zoom-value-display">{zoomLevel}%</span>
      </div>

      {/* Right side - Zoom In */}
      <button className="control-icon-btn zoom-in-btn" onClick={onZoomIn} title="Zoom In" aria-label="Zoom in">
        <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
          <line x1="10" y1="7" x2="10" y2="13" stroke="currentColor" strokeWidth="2" />
          <line x1="7" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth="2" />
          <line x1="14" y1="14" x2="20" y2="20" stroke="currentColor" strokeWidth="2" />
        </svg>
      </button>

      {/* Home/Reset Button */}
      <button className="control-icon-btn reset-btn" onClick={onReset} title="Reset View (Home)" aria-label="Reset view">
        {/* <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
          <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
        </svg> */}

        <RiResetRightLine size={24} />
      </button>

      {/* Fullscreen Button */}
      {isWeb && (
        <button
          className="control-icon-btn fullscreen-btn"
          onClick={handleFullscreenClick}
          title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          aria-label="Toggle fullscreen"
        >
          {isFullscreen ? <RiFullscreenExitLine size={24} /> : <RiFullscreenLine size={24} />}
        </button>
      )}
      {/* download */}
      {isWeb && (
        <button
          className="control-icon-btn download-btn"
          onClick={handleDownloadClick}
          title="Download Image"
          aria-label="Download image"
          disabled={isDownloading}
        >
          {isDownloading ? <AiOutlineLoading3Quarters size={24} className="spinner-icon" /> : <FaDownload size={24} />}
        </button>
      )}

      {/* Download Dialog - shadcn/ui Modal */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent className="max-w-[450px]">
          <DialogHeader>
            <DialogTitle className={normalInter}>Select Image Quality</DialogTitle>
            <DialogDescription className={medium}>Choose which resolution you want to download</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            {/* Normal Option */}
            <button
              className="p-5 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 cursor-pointer transition-all duration-300 text-center hover:border-orange-600 dark:hover:border-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/30 hover:-translate-y-1.5 hover:shadow-lg"
              onClick={() => downloadImage(downloadUrl, "normal")}
            >
              <div className={`font-bold text-gray-950 dark:text-white mb-2 text-sm ${normalInter}`}>Standard Quality</div>
              <div className={`text-xs text-gray-600 dark:text-gray-400 leading-relaxed ${medium}`}>Normal resolution image</div>
            </button>

            {/* High Option */}
            <button
              className="p-5 border-2 border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-900 cursor-pointer transition-all duration-300 text-center hover:border-orange-600 dark:hover:border-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/30 hover:-translate-y-1.5 hover:shadow-lg"
              onClick={() => downloadImage(downloadUrlHigh, "high")}
            >
              <div className={`font-bold text-gray-950 dark:text-white mb-2 text-sm ${normalInter}`}>High Quality</div>
              <div className={`text-xs text-gray-600 dark:text-gray-400 leading-relaxed ${medium}`}>High resolution image</div>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ControlBar;
